import * as Sentry from '@sentry/react';
import { createRouter } from '@tanstack/react-router';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { ConvexReactClient } from 'convex/react';
import { ClerkProvider, useAuth, useUser } from '@clerk/react';
import { enUS } from '@clerk/localizations';
import { useEffect } from 'react';
import { routeTree } from './routeTree.gen';
import { isExpectedError } from './instrument';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { frCA } from '@/lib/frCA';
import { CLERK_PUBLISHABLE_KEY, CONVEX_URL } from '@/lib/env';

function RouterErrorFallback({ error }: { error: unknown }) {
  useEffect(() => {
    if (!isExpectedError(error)) Sentry.captureException(error);
  }, [error]);
  return <p>{m.tutor_error_unexpected()}</p>;
}

let _convex: ConvexReactClient | undefined;
function getConvex(url: string) {
  return (_convex ??= new ConvexReactClient(url));
}

export function getRouter() {
  const convex = getConvex(CONVEX_URL);

  const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: (err) => <RouterErrorFallback error={err.error} />,
    defaultNotFoundComponent: () => <p>{m.tutor_error_notFound()}</p>,
    InnerWrap: function InnerWrapWithProviders({ children }) {
      const clerkLocalization = getLocale() === 'fr' ? frCA : enUS;

      return (
        <ClerkProvider
          publishableKey={CLERK_PUBLISHABLE_KEY}
          localization={clerkLocalization}
          signInFallbackRedirectUrl="/"
        >
          <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
            <SentryUserContext />
            {children}
          </ConvexProviderWithClerk>
        </ClerkProvider>
      );
    },
  });

  Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(router));

  return router;
}

function SentryUserContext() {
  const { user } = useUser();
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        // Phone-only users (SMS sign-in) have no email — synthesize one so Sentry
        // can still associate events with a recognizable identifier.
        // The `.invalid` TLD (RFC 2606) signals this is not a real address.
        email:
          user.primaryEmailAddress?.emailAddress ??
          (user.primaryPhoneNumber?.phoneNumber
            ? `phone+${user.primaryPhoneNumber.phoneNumber}@sms.invalid`
            : undefined),
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);
  return null;
}
